import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { devError } from "@/lib/devLog";
import { CATEGORIES, SPECIAL_TAGS } from "@/lib/constants";
import SEO from "@/components/SEO";
import FeaturesDialog from "@/components/FeaturesDialog";
import { track as trackAnalytics } from "@/lib/analytics";
import EventCard from "@/components/EventCard";
import LocationAutocomplete from "@/components/LocationAutocomplete";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Trophy, MapPin, Calendar as CalIcon, Search, X, Filter, Lock, UserPlus, Sparkles, Eye, Heart, Share2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { b64ToSrc } from "@/lib/img";

// iter58 — French Overseas territories exposed as a quick-pick selector
// in the Home hero. Order approximates traffic priority so the most
// active regions surface first. Values are matched by the backend's
// tolerant country filter (regex + city-in-region fallback) so both
// bare names and "Martinique (France)"-style aliases resolve correctly.
const TERRITOIRES = [
    "Martinique",
    "Guadeloupe",
    "Guyane",
    "La Réunion",
    "Mayotte",
    "Nouvelle-Calédonie",
    "Polynésie française",
    "Saint-Barthélemy",
    "Saint-Martin",
    "Saint-Pierre-et-Miquelon",
    "Wallis-et-Futuna",
    "France métropolitaine",
];

/**
 * iter45 — Per-category icon colour palette. Applied only when the
 * category chip is NOT active (active chip inherits the white gradient
 * text). Keeping the map local to Home.jsx keeps the surface change
 * small — no touch to constants.js which is consumed by many other
 * views. Values are Tailwind text-* utility classes.
 */
const CATEGORY_COLORS = {
    "vide-grenier":   "text-amber-600",
    "exposition":     "text-fuchsia-500",
    "conference":     "text-indigo-500",
    "theatre":        "text-rose-500",
    "bons-plans":     "text-yellow-500",
    "restaurant":     "text-orange-500",
    "menu-du-jour":   "text-red-500",
    "sport":          "text-lime-600",
    "decouverte":     "text-teal-500",
    "art-creatif":    "text-pink-500",
    "green-attitude": "text-emerald-500",
    "musique":        "text-purple-500",
    "danse":          "text-fuchsia-600",
    "litterature":    "text-slate-600",
    "jeu-video":      "text-violet-500",
    "grand-coeur":    "text-red-400",
    "marche":         "text-yellow-600",
    "bien-etre":      "text-emerald-400",
    "voyage":         "text-sky-500",
    "nautique":       "text-cyan-500",
    "cinema":         "text-blue-500",
    "jeu":            "text-orange-400",
    "soiree":         "text-purple-600",
    "evenementiel":   "text-blue-600",
    "formation":      "text-indigo-600",
    "formalites":     "text-gray-500",
    "shopping":       "text-pink-400",
    "emploi":         "text-blue-700",
};

export default function Home() {
    const { user, setUser } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    // Restore the previous search/filter state when the user comes back to
    // the home page (e.g. after viewing an event in detail). We keep the
    // serialized state in sessionStorage so it survives a single navigation
    // round-trip but is cleared when the browser tab closes.
    const SAVED_KEY = "discoveur:home:filters:v1";
    const SCROLL_KEY = "discoveur:home:scroll:v1";
    const saved = (() => {
        try { return JSON.parse(sessionStorage.getItem(SAVED_KEY) || "null"); }
        catch { return null; }
    })();
    // URL params take priority over sessionStorage (shareable links).
    const urlParams = new URLSearchParams(location.search);
    const fromUrl = {
        keyword: urlParams.get("q") || undefined,
        category: urlParams.get("cat") || undefined,
        city: urlParams.get("city") || undefined,
        country: urlParams.get("country") || undefined,
        specialTag: urlParams.get("tag") || undefined,
        freeOnly: urlParams.get("free") === "1" ? true : undefined,
        heureMin: urlParams.get("hmin") || undefined,
        heureMax: urlParams.get("hmax") || undefined,
        date: urlParams.get("d") || undefined,
    };
    const hasUrlFilters = Object.values(fromUrl).some(v => v !== undefined);

    const initial = (k, fallback) => fromUrl[k] !== undefined ? fromUrl[k] : (saved?.[k] ?? fallback);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [date, setDate] = useState(initial("date", null) ? new Date(initial("date", null)) : null);
    const [keyword, setKeyword] = useState(initial("keyword", ""));
    const [category, setCategory] = useState(initial("category", null));
    // City/country are intentionally NOT pre-filled from the user profile
    // anymore — the user picks fresh location on every visit to discover
    // anywhere (otherwise the home is "locked" on their home town).
    const [city, setCity] = useState(initial("city", ""));
    const [country, setCountry] = useState(initial("country", ""));
    const [freeOnly, setFreeOnly] = useState(!!initial("freeOnly", false));
    const [specialTag, setSpecialTag] = useState(initial("specialTag", null));
    const [heureMin, setHeureMin] = useState(initial("heureMin", ""));
    const [heureMax, setHeureMax] = useState(initial("heureMax", ""));
    const [showFilters, setShowFilters] = useState(!!saved?.showFilters);
    const [registerPrompt, setRegisterPrompt] = useState(false);
    const [catCounts, setCatCounts] = useState({});
    const [stats, setStats] = useState({ users: 0, events_active: 0 });
    const [topUsers, setTopUsers] = useState([]);
    const [latestPov, setLatestPov] = useState(null);
    const [regionMode, setRegionMode] = useState(!!saved?.regionMode);
    const [allLocations, setAllLocations] = useState(!!saved?.allLocations);

    // iter53 — Reset SEO to app-wide defaults on Home. Ensures any per-page
    // JSON-LD/canonical set by EventDetail/PovDetail is cleared when the
    // user navigates back to the home feed.
    // iter53 — migrated to <SEO /> component (rendered in JSX below).
    const isAdmin = user?.role === "admin";
    const [featuresOpen, setFeaturesOpen] = useState(false);
    const restoredRef = useRef(false);

    // Skip the user-city default if URL has filters too.
    useEffect(() => {
        if (saved || hasUrlFilters) return;
        if (user?.location_city && !city) {
            setCity(user.location_city);
            setCountry(user.location_country || "");
        }
        // eslint-disable-next-line
    }, [user?.id]);

    // ─── Share my selection ────────────────────────────────────────────
    // Only admins and paid subscribers (VIP/Silver/Gold/Premium) can
    // share a curated URL of their current filters. The share text always
    // mentions "discoveuriste abonné" (or "ambassadeur" for admins) and
    // can include the prénom of the sharer if their profile is public.
    const isSubscribed = user && (user.pro_plan && user.pro_plan !== "free")
        && (user.subscription_active || user.role === "admin");
    const canShareSelection = !!user && (user.role === "admin" || isSubscribed);
    const profileIsPublic = !!user?.show_published_pseudo;
    const hasActiveFilters = !!(keyword || date || category || specialTag || freeOnly || heureMin || heureMax);

    const buildShareUrl = () => {
        const u = new URL(window.location.origin + "/");
        if (keyword) u.searchParams.set("q", keyword);
        if (category) u.searchParams.set("cat", category);
        if (city) u.searchParams.set("city", city);
        if (country && regionMode) u.searchParams.set("country", country);
        if (specialTag) u.searchParams.set("tag", specialTag);
        if (freeOnly) u.searchParams.set("free", "1");
        if (heureMin) u.searchParams.set("hmin", heureMin);
        if (heureMax) u.searchParams.set("hmax", heureMax);
        if (date) u.searchParams.set("d", format(date, "yyyy-MM-dd"));
        return u.toString();
    };

    const buildShareText = () => {
        const prenom = profileIsPublic ? (user?.prenom || user?.pseudo || "") : "";
        const role = user?.role === "admin"
            ? "ambassadeur Discoveur"
            : (user?.pro_plan === "gold" || user?.pro_plan === "premium")
                ? "organisateur Discoveur"
                : "discoveuriste abonné";
        const by = prenom
            ? `Évènements sélectionnés par ${prenom}, ${role}`
            : `Évènements sélectionnés par un ${role}`;
        const subline = (user?.pro_plan === "gold" || user?.pro_plan === "premium")
            ? " — suis-moi pour ne rien manquer 🎟️"
            : "";
        return `${by}${subline}\n\nDécouvre la sélection complète sur Discoveur :`;
    };

    const shareSelection = async () => {
        const url = buildShareUrl();
        const text = buildShareText();
        try {
            if (navigator.share) {
                await navigator.share({ title: "Discoveur — Ma sélection", text, url });
            } else {
                await navigator.clipboard.writeText(`${text}\n${url}`);
                toast.success("Lien copié dans le presse-papier 🔗");
            }
        } catch (e) {
            if (e?.name !== "AbortError") {
                try {
                    await navigator.clipboard.writeText(`${text}\n${url}`);
                    toast.success("Lien copié dans le presse-papier 🔗");
                } catch { toast.error("Partage impossible"); }
            }
        }
    };
    // ───────────────────────────────────────────────────────────────────

    // Fetch dynamic category counts (for ordering) + stats
    useEffect(() => {
        api.get("/meta/category-counts")
            .then(r => setCatCounts(r.data || {}))
            .catch(() => setCatCounts({}));
        api.get("/meta/stats")
            .then(r => setStats(r.data || { users: 0, events_active: 0 }))
            .catch(() => {});
        api.get("/meta/top-discoveuristes?limit=3")
            .then(r => setTopUsers(Array.isArray(r.data) ? r.data : []))
            .catch(() => setTopUsers([]));
        api.get("/articles?sort=recent")
            .then(r => setLatestPov(Array.isArray(r.data) && r.data.length ? r.data[0] : null))
            .catch(() => setLatestPov(null));
    }, []);

    const orderedCategories = [...CATEGORIES].sort((a, b) => {
        const ca = catCounts[a.id] || 0;
        const cb = catCounts[b.id] || 0;
        if (cb !== ca) return cb - ca;
        return a.label.localeCompare(b.label, "fr");
    });

    // iter61 — Landing page limit bumped to 30 per user request:
    // "sur la page TOUS tu peux limiter les résultats à 30 et proposer
    //  une pagination pour continuer à scroller." Higher page size
    // reduces the number of "Voir plus" clicks needed to see the whole
    // catalogue while keeping the initial payload light.
    const PAGE_SIZE = 30;
    const [pageNum, setPageNum] = useState(1);
    const [totalEvents, setTotalEvents] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const pendingRestorePagesRef = useRef(0);

    const _buildParams = (targetPage) => {
        const params = new URLSearchParams();
        if (date) params.set("date_from", format(date, "yyyy-MM-dd"));
        if (date) params.set("date_to", format(date, "yyyy-MM-dd"));
        if (keyword) params.set("keyword", keyword);
        if (category) params.set("category", category);
        if (isAdmin && allLocations) {
            params.set("all_locations", "true");
        } else if (regionMode && country) {
            params.set("country", country);
        } else if (city) {
            params.set("city", city);
        }
        if (freeOnly) params.set("free_only", "true");
        if (specialTag) params.set("special_tag", specialTag);
        if (heureMin) params.set("heure_min", heureMin);
        if (heureMax) params.set("heure_max", heureMax);
        params.set("page", String(targetPage));
        params.set("limit", String(PAGE_SIZE));
        return params;
    };

    const fetchEvents = async () => {
        setLoading(true);
        try {
            const params = _buildParams(1);
            const r = await api.get(`/events?${params.toString()}`);
            // iter60 — Response is now `{items, total, page, limit, has_more}`.
            // Guard against the legacy array shape in case an older
            // backend answers during rollout.
            const payload = r?.data || {};
            const items = Array.isArray(payload)
                ? payload
                : (Array.isArray(payload.items) ? payload.items : []);
            const total = Array.isArray(payload) ? items.length : (payload.total ?? items.length);
            const more = Array.isArray(payload) ? false : !!payload.has_more;
            setEvents(items);
            setPageNum(1);
            setTotalEvents(total);
            setHasMore(more);
            // Track only actual search queries (with keyword), not
            // background filter refetches. Zero-result queries surface
            // "what people look for but don't find" in the dashboard.
            if (keyword && keyword.trim()) {
                try {
                    trackAnalytics("search", {
                        query: keyword.trim().slice(0, 120),
                        results_count: total,
                        category: category || null,
                        city: city || null,
                    });
                } catch (err) { devError("trackAnalytics search failed", err); }
            }
            // If we're returning from an event detail, replay the extra
            // pages the user had loaded so scroll restoration lands on
            // the same card.
            const savedPages = pendingRestorePagesRef.current;
            pendingRestorePagesRef.current = 0;
            if (savedPages > 1 && more) {
                await _replayPages(savedPages, items);
            }
        } catch (e) {
            if (process.env.NODE_ENV === "development") {
                console.error("fetchEvents error", e);
            }
            setEvents([]);
            setHasMore(false);
            const friendly = e?._friendly;
            const detail = e?.response?.data?.detail;
            if (friendly) {
                toast.error(friendly);
            } else if (detail) {
                toast.error(`Erreur chargement : ${detail}`);
            } else {
                toast.error("Impossible de charger les événements pour le moment. Réessaie dans un instant.");
            }
        }
        setLoading(false);
    };

    const _replayPages = async (targetPageCount, seed) => {
        // Sequentially load pages 2..targetPageCount and merge into events.
        let acc = [...seed];
        let stillMore = true;
        for (let p = 2; p <= targetPageCount && stillMore; p++) {
            try {
                const params = _buildParams(p);
                const r = await api.get(`/events?${params.toString()}`);
                const payload = r?.data || {};
                const items = Array.isArray(payload.items) ? payload.items : [];
                acc = acc.concat(items);
                stillMore = !!payload.has_more;
                setPageNum(p);
                setHasMore(stillMore);
            } catch (err) {
                devError("replay page failed", err);
                break;
            }
        }
        setEvents(acc);
    };

    const loadMore = async () => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        try {
            const next = pageNum + 1;
            const params = _buildParams(next);
            const r = await api.get(`/events?${params.toString()}`);
            const payload = r?.data || {};
            const items = Array.isArray(payload.items) ? payload.items : [];
            setEvents((prev) => [...prev, ...items]);
            setPageNum(next);
            setHasMore(!!payload.has_more);
            setTotalEvents(payload.total ?? totalEvents);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(detail || "Chargement supplémentaire impossible");
        }
        setLoadingMore(false);
    };


    useEffect(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        fetchEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, category, freeOnly, specialTag, city, regionMode, allLocations]);

    // Debounced live search: 250 ms idle is short enough to feel instant
    // while preventing a request on every keystroke.
    useEffect(() => {
        const t = setTimeout(() => { fetchEvents(); }, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [keyword]);

    // Persist the current search/filter state so navigating to an event
    // detail and coming back preserves everything the user had typed.
    useEffect(() => {
        try {
            sessionStorage.setItem(SAVED_KEY, JSON.stringify({
                date: date ? date.toISOString() : null,
                keyword, category, city, country, freeOnly, specialTag,
                heureMin, heureMax, showFilters, regionMode, allLocations,
            }));
        } catch (e) { devError("save filters sessionStorage failed:", e); }
    }, [date, keyword, category, city, country, freeOnly, specialTag,
        heureMin, heureMax, showFilters, regionMode, allLocations]);

    // Restore scroll position once events are rendered after a back-nav.
    // We restore on the FIRST loaded render after mount regardless of
    // whether saved filters were rehydrated — what matters is that the
    // user returned to the same listing page (the scroll Y is stored in
    // sessionStorage by the cleanup effect below).
    useEffect(() => {
        if (restoredRef.current) return;
        if (loading) return;
        const raw = sessionStorage.getItem(SCROLL_KEY);
        const y = parseInt(raw || "0", 10);
        if (!Number.isNaN(y) && y > 0) {
            // wait one frame so the event grid is laid out before the jump
            requestAnimationFrame(() =>
                requestAnimationFrame(() =>
                    window.scrollTo({ top: y, behavior: "auto" })));
        }
        restoredRef.current = true;
        // eslint-disable-next-line
    }, [loading, events.length]);

    // Save scroll position right before the user navigates away (e.g. to
    // an event detail page or any other route). Also stash the number
    // of pages the user has loaded so we can replay them on back-nav
    // and land at the exact card they clicked.
    useEffect(() => {
        const save = () => {
            try {
                sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
                sessionStorage.setItem(SCROLL_KEY + "_pages", String(pageNum));
            }
            catch (e) { devError("save scroll position failed:", e); }
        };
        window.addEventListener("beforeunload", save);
        return () => {
            save();
            window.removeEventListener("beforeunload", save);
        };
    }, [location.pathname, pageNum]);

    // On first mount, if a paginated back-nav is pending, prime the
    // fetch loop to replay the pages that were loaded before.
    useEffect(() => {
        try {
            const saved = parseInt(sessionStorage.getItem(SCROLL_KEY + "_pages") || "0", 10);
            if (!Number.isNaN(saved) && saved > 1) {
                pendingRestorePagesRef.current = saved;
            }
        } catch (_) { /* sessionStorage disabled */ }
        // eslint-disable-next-line
    }, []);

    const updateLocation = async (v) => {
        try {
            if (v?.city) {
                // Selection from suggestions: city + country
                setCity(v.city);
                setCountry(v.country || "");
                if (user) {
                    try {
                        const r = await api.put("/profile", {
                            location_city: v.city,
                            location_country: v.country || "",
                        });
                        if (r?.data) setUser(r.data);
                        toast.success("Localisation mise à jour");
                    } catch (err) {
                        if (process.env.NODE_ENV === "development") {
                            console.warn("profile sync failed", err);
                        }
                    }
                }
            }
            // Note: typing without selecting does NOT update city filter (was source of crashes
            // due to per-keystroke fetchEvents). User must pick a suggestion.
        } catch (err) {
            console.error("updateLocation error", err);
        }
    };

    const clearLocation = () => {
        setCity("");
        setCountry("");
    };

    const handleFollow = async (id) => {
        try {
            await api.post(`/events/${id}/follow`);
            toast.success("Événement suivi");
            fetchEvents();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };
    const handleJoin = async (id) => {
        try {
            await api.post(`/events/${id}/join`, { activate_exchanges: false, add_to_agenda: false });
            toast.success("Vous avez rejoint l'événement !");
            fetchEvents();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 space-y-6">
            <SEO />
            {/* Hero — iter46: tighter vertical padding so the hero card
                takes less viewport height. Top padding trimmed (-1 pt)
                and bottom padding trimmed after the counter chips so
                the event grid + top-3 discoveuristes + POV card
                surface higher on desktop AND on mobile/tablet. */}
            <div className="rounded-3xl bg-discover-gradient px-5 md:px-8 pt-4 md:pt-5 pb-3 md:pb-4 text-white shadow-xl relative overflow-hidden">
                <div className="absolute -right-12 -bottom-12 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
                <h1 className="text-xl md:text-2xl font-black" data-testid="home-greeting">
                    Bonjour {user?.prenom || "discoveuriste"}
                </h1>
                {/* iter53 — Concise hero copy. The full feature walkthrough
                    (search, publish, join, chat, premium…) lives in a
                    modal opened by the "ici" link so first-time visitors
                    aren't overwhelmed. Detailed territory + intent keywords
                    kept in the Footer manifesto for SEO.
                    Line-height tightened (leading-[1.55]) at user's request
                    so the block stays readable but sits ~0.5pt closer. */}
                <h2 className="mt-2 text-base md:text-lg font-bold text-white/95 leading-[1.55]"
                    data-testid="home-seo-tagline">
                    Discoveur – La plateforme collaborative des sorties, activités et événements des Outre-mer.
                </h2>
                <p className="mt-2 text-sm md:text-base text-white/95 max-w-3xl leading-[1.55]"
                   data-testid="home-seo-description">
                    <span aria-hidden="true">📅 </span>
                    Avec Discoveur découvrez quoi faire toute l'année dans les
                    territoires ultramarins et faisons rayonner nos talents !
                </p>
                <p className="mt-2 text-sm text-white/90 max-w-3xl leading-[1.55]"
                   data-testid="home-seo-catalog">
                    Trouvez facilement des <strong>événements</strong>,{" "}
                    <strong>loisirs</strong>, <strong>sorties culturelles</strong>,{" "}
                    <strong>activités sportives</strong>, <strong>marchés</strong>,{" "}
                    <strong>concerts</strong>, <strong>expositions</strong>,{" "}
                    <strong>brunchs</strong>, <strong>restaurants</strong> et bien plus.
                    Découvrez toutes les fonctionnalités de Discoveur{" "}
                    <button type="button"
                        onClick={() => setFeaturesOpen(true)}
                        className="underline decoration-white/70 underline-offset-2 font-semibold text-white hover:text-white/80 focus:outline-none focus:ring-2 focus:ring-white/60 rounded"
                        data-testid="home-features-link">
                        ici
                    </button>
                    .
                </p>
                <FeaturesDialog open={featuresOpen} onOpenChange={setFeaturesOpen} />
                {user && (
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-white/90">
                        <span className="inline-flex items-center gap-1 text-sm" data-testid="home-location">
                            <MapPin size={14} />{user.location_city || "Localisation à définir"}
                        </span>
                        <span className="inline-flex items-center gap-1 text-sm font-semibold bg-white/20 backdrop-blur rounded-full px-3 py-1" data-testid="home-grade">
                            <Trophy size={14} />{user.grade?.name} - {user.points || 0} pts
                        </span>
                    </div>
                )}
                <div className="mt-3 max-w-md space-y-2">
                    <LocationAutocomplete
                        value={city ? (country ? `${city}, ${country}` : city) : ""}
                        onChange={updateLocation}
                        placeholder={city ? "Changer de ville..." : "Filtrer par ville (optionnel)"}
                        testid="home-location-input"
                    />
                    {/* iter58 — Territory-wide search selector. Always visible
                        (below the city filter, per user request). Picking a
                        territory clears the city filter and switches to
                        `regionMode=true` so the backend returns every event
                        across that whole territory. Admins keep their own
                        "toutes les localisations" toggle further down. */}
                    <select
                        value={regionMode && country ? country : ""}
                        onChange={(e) => {
                            const t = e.target.value;
                            if (!t) {
                                setRegionMode(false);
                                return;
                            }
                            setCity("");
                            setCountry(t);
                            setRegionMode(true);
                        }}
                        className="w-full rounded-full bg-white/95 text-foreground px-4 py-2 text-sm font-medium border-0 focus:outline-none focus:ring-2 focus:ring-white/60"
                        data-testid="home-territory-select">
                        <option value="">Ou explorer tout un territoire…</option>
                        {TERRITOIRES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                    {country && !regionMode && (
                        <label className="flex items-center gap-2 text-sm text-white/95 cursor-pointer select-none"
                            data-testid="home-region-toggle-label">
                            <Checkbox
                                checked={regionMode}
                                onCheckedChange={(v) => setRegionMode(!!v)}
                                className="border-white data-[state=checked]:bg-white data-[state=checked]:text-primary"
                                data-testid="home-region-toggle" />
                            Voir tous les événements de {country}
                        </label>
                    )}
                    {city && (
                        <button onClick={clearLocation}
                            className="text-xs text-white/90 underline hover:text-white"
                            data-testid="home-location-clear">
                            Voir tous les événements (sans filtre ville)
                        </button>
                    )}
                    {isAdmin && (
                        <label className="flex items-center gap-2 text-sm text-white/95 cursor-pointer select-none"
                            data-testid="home-admin-all-loc-label">
                            <Checkbox
                                checked={allLocations}
                                onCheckedChange={(v) => setAllLocations(!!v)}
                                className="border-white data-[state=checked]:bg-white data-[state=checked]:text-primary"
                                data-testid="home-admin-all-loc" />
                            Mode admin · Voir toutes les publications (toutes localisations)
                        </label>
                    )}
                </div>
                {/* Counter of registered Discoveuristes */}
                <div className="mt-2.5 flex flex-wrap gap-3 text-xs text-white/90">
                    <span className="px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm" data-testid="home-stat-users">
                        🎉 {stats.users.toLocaleString("fr-FR")} discoveuriste{stats.users > 1 ? "s" : ""} inscrit{stats.users > 1 ? "s" : ""}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm" data-testid="home-stat-events">
                        📍 {stats.events_active.toLocaleString("fr-FR")} événement{stats.events_active > 1 ? "s" : ""} à venir
                    </span>
                </div>
            </div>

            {/* Filter bar (search + date + filters) — moved right under the
                hero stats so users can refine results before browsing. */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex-1 min-w-[200px] relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={keyword} onChange={(e) => setKeyword(e.target.value)}
                        placeholder="Rechercher (titre, lieu, menu, texte du flyer...)"
                        className="pl-9 rounded-full" data-testid="home-search" />
                    {keyword && (
                        <button onClick={() => setKeyword("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            data-testid="home-search-clear" aria-label="Effacer">
                            <X size={14} />
                        </button>
                    )}
                </div>
                <Popover>
                    <PopoverTrigger asChild>
                        <Button variant="outline" className="rounded-full" data-testid="home-date-btn">
                            <CalIcon size={14} className="mr-1.5" />
                            {date ? format(date, "dd/MM/yyyy") : "Choisir date"}
                            {date && <X size={12} className="ml-1.5" onClick={(e) => { e.stopPropagation(); setDate(null); }} />}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="rounded-2xl p-0" align="start">
                        <Calendar mode="single" selected={date} onSelect={setDate} locale={fr} />
                    </PopoverContent>
                </Popover>
                <Button variant={showFilters ? "default" : "outline"}
                    onClick={() => setShowFilters(!showFilters)}
                    className="rounded-full" data-testid="home-filters-btn">
                    <Filter size={14} className="mr-1.5" />Filtres
                </Button>
                {/* iter51 — Elevated "Gratuit" quick toggle so users
                    don't need to open the Filters drawer to find it. */}
                <Button variant={freeOnly ? "default" : "outline"}
                    onClick={() => setFreeOnly(!freeOnly)}
                    className={`rounded-full ${freeOnly ? "bg-emerald-600 text-white hover:bg-emerald-700" : "border-emerald-400 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"}`}
                    data-testid="home-free-quick">
                    🎟️ Gratuit
                </Button>
                {(keyword || date || category || specialTag || freeOnly || heureMin || heureMax) && (
                    <Button variant="ghost"
                        onClick={() => {
                            setKeyword(""); setDate(null); setCategory(null);
                            setSpecialTag(null); setFreeOnly(false);
                            setHeureMin(""); setHeureMax("");
                            try { sessionStorage.removeItem(SCROLL_KEY); }
                            catch (e) { devError("clear scroll storage failed:", e); }
                        }}
                        className="rounded-full text-xs text-muted-foreground hover:text-foreground"
                        data-testid="home-reset-filters">
                        <X size={13} className="mr-1" />Effacer
                    </Button>
                )}
                {canShareSelection && hasActiveFilters && (
                    <Button variant="outline"
                        onClick={shareSelection}
                        className="rounded-full text-xs border-amber-400 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                        data-testid="home-share-selection">
                        <Share2 size={13} className="mr-1.5" />
                        {user?.role === "admin"
                            ? "Partager (ambassadeur)"
                            : (user?.pro_plan === "gold" || user?.pro_plan === "premium")
                                ? "Partager mes événements"
                                : "Partager ma sélection"}
                    </Button>
                )}
            </div>

            {showFilters && (
                <div className="rounded-3xl border border-border p-4 space-y-3 bg-card">
                    <div className="flex flex-wrap items-center gap-2">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={freeOnly} onCheckedChange={(v) => setFreeOnly(!!v)} data-testid="filter-free" />
                            <span>Gratuit uniquement</span>
                        </label>
                        <Input type="time" step="1800" value={heureMin} onChange={(e) => setHeureMin(e.target.value)}
                            className="rounded-full w-32" data-testid="filter-time-min" placeholder="Début min" />
                        <span className="text-muted-foreground">à</span>
                        <Input type="time" step="1800" value={heureMax} onChange={(e) => setHeureMax(e.target.value)}
                            className="rounded-full w-32" data-testid="filter-time-max" placeholder="Début max" />
                        <Button size="sm" onClick={fetchEvents} className="rounded-full bg-discover-gradient text-white" data-testid="filter-apply">Appliquer</Button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {SPECIAL_TAGS.map(t => (
                            <button key={t.id} onClick={() => setSpecialTag(specialTag === t.id ? null : t.id)}
                                data-testid={`tag-${t.id}`}
                                className={`text-xs px-3 py-1 rounded-full border transition ${specialTag === t.id ? "bg-discover-gradient text-white border-transparent" : "border-border hover:bg-accent"}`}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Category icons — iter45: coloured icons per category,
                icons 1pt smaller (18px), tighter gaps (gap-1.5), tighter
                line-height (leading-none) and 15 columns on lg so the 30
                categories fit on exactly 2 rows on desktop. */}
            <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
                <div className="flex gap-1.5 md:grid md:gap-1.5 md:grid-cols-10 lg:grid-cols-[repeat(15,minmax(0,1fr))]">
                    <button onClick={() => setCategory(null)} data-testid="cat-all"
                        className={`flex-shrink-0 flex flex-col items-center gap-0.5 p-2 rounded-2xl transition w-16 ${!category ? "bg-discover-gradient text-white shadow-md" : "hover:bg-accent"}`}>
                        <span className="text-lg leading-none">⭐</span>
                        <span className="text-[11px] font-semibold leading-none">Tous</span>
                    </button>
                    {orderedCategories.map(({ id, label, icon: Icon }) => {
                        const count = catCounts[id] || 0;
                        const isActive = category === id;
                        const iconColor = CATEGORY_COLORS[id] || "text-slate-500";
                        return (
                            <button key={id} onClick={() => setCategory(isActive ? null : id)}
                                data-testid={`cat-${id}`}
                                className={`flex-shrink-0 flex flex-col items-center gap-0.5 p-2 rounded-2xl transition w-16 relative ${isActive ? "bg-discover-gradient text-white shadow-md" : "hover:bg-accent"}`}>
                                <Icon size={18} className={isActive ? "text-white" : iconColor} />
                                <span className="text-[10px] font-semibold text-center leading-none line-clamp-2">{label}</span>
                                {count > 0 && (
                                    <span className={`absolute -top-1 -right-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center ${isActive ? "bg-white text-primary" : "bg-discover-gradient text-white"}`}
                                        data-testid={`cat-count-${id}`}>
                                        {count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Top 3 discoveuristes (excludes admin) + latest POV card
                — kept side-by-side at every breakpoint and intentionally
                slim so the event grid surfaces higher on the page. */}
            {(topUsers.length > 0 || latestPov) && (
                <div className="grid grid-cols-2 gap-2">
                    {topUsers.length > 0 && (
                        <div className="discover-card p-2.5" data-testid="home-top-discoveuristes">
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <Trophy size={13} className="text-amber-500 flex-shrink-0" />
                                <h2 className="font-black text-xs leading-tight">Top 3 discoveuristes</h2>
                            </div>
                            <div className="flex flex-col gap-1">
                                {topUsers.map((u, idx) => (
                                    <div key={u.id} className="flex items-center gap-1.5"
                                        data-testid={`top-user-${idx}`}>
                                        <div className={`w-4 h-4 rounded-full flex items-center justify-center font-black text-white text-[9px] flex-shrink-0 ${idx === 0 ? "bg-amber-500" : idx === 1 ? "bg-slate-400" : "bg-orange-400"}`}>
                                            {idx + 1}
                                        </div>
                                        <div className="flex-1 min-w-0 leading-tight flex items-baseline gap-1">
                                            <span className="font-bold truncate text-[11px]">{u.pseudo}</span>
                                            <span className="text-[10px] text-muted-foreground flex-shrink-0">· {u.points} pts</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {latestPov && (
                        <Link to={`/pov/${latestPov.id}`} data-testid="home-latest-pov"
                            className="discover-card overflow-hidden group flex flex-row relative cursor-pointer hover:shadow-lg transition-shadow">
                            {(latestPov.cover_base64 || (latestPov.photos && latestPov.photos[0])) ? (
                                <img src={b64ToSrc(latestPov.cover_base64 || latestPov.photos[0])}
                                    alt={latestPov.title}
                                    className="w-16 h-full object-cover flex-shrink-0 transition-transform group-hover:scale-105" />
                            ) : (
                                <div className="w-16 flex-shrink-0 bg-discover-gradient flex items-center justify-center">
                                    <Sparkles className="text-white/90" size={20} />
                                </div>
                            )}
                            <div className="p-2 flex-1 flex flex-col min-w-0">
                                <span className="text-[8px] font-bold uppercase tracking-wider text-primary flex items-center gap-1">
                                    <Sparkles size={9} />Dernier POV
                                </span>
                                <h3 className="font-black text-[11px] mt-0.5 line-clamp-2 leading-tight" data-testid="home-latest-pov-title">
                                    {latestPov.title}
                                </h3>
                                <div className="flex items-center gap-1.5 mt-auto pt-1 flex-wrap">
                                    {latestPov.rating_count > 0 && (
                                        <span className="flex items-center gap-0.5 text-pink-500 text-[10px] font-semibold"
                                            data-testid="home-latest-pov-rating">
                                            <Heart size={9} className="fill-pink-500" />
                                            {latestPov.rating_avg}/5
                                        </span>
                                    )}
                                    <span className="flex items-center gap-0.5 text-muted-foreground text-[10px]">
                                        <Eye size={9} />{latestPov.views_count || 0}
                                    </span>
                                    <span data-testid="home-latest-pov-cta"
                                        className="ml-auto text-[9px] font-bold text-primary">Lire →</span>
                                </div>
                            </div>
                        </Link>
                    )}
                </div>
            )}

            {/* Events */}
            <div>
                <h2 className="text-xl font-black mb-3 flex items-center gap-2"
                    data-testid="home-events-heading">
                    {loading ? (
                        <>
                            <span className="inline-block h-4 w-4 rounded-full border-2 border-primary border-t-transparent animate-spin"
                                data-testid="home-loading-spinner" aria-hidden="true" />
                            <span data-testid="home-loading-label">Recherche en cours…</span>
                        </>
                    ) : (
                        <>
                            {/* iter60 — Show "X/Y trouvés" while the user is
                                paginating so they know how much more the
                                filter matched beyond the currently rendered
                                cards. */}
                            {totalEvents > events.length
                                ? <>{events.length} sur {totalEvents} évènement{totalEvents > 1 ? "s" : ""} affiché{events.length > 1 ? "s" : ""}</>
                                : <>{events.length} évènement{events.length > 1 ? "s" : ""} trouvé{events.length > 1 ? "s" : ""}</>}
                        </>
                    )}
                </h2>
                {loading ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[1, 2, 3].map(i => <div key={i} className="discover-card h-72 animate-pulse" />)}
                    </div>
                ) : events.length === 0 ? (
                    <div className="rounded-3xl border-2 border-dashed border-border p-12 text-center text-muted-foreground" data-testid="home-empty">
                        Aucun événement pour le moment. Soyez le premier a en publier !
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="home-events-grid">
                        {events.map(e => (
                            <div key={e.id} className={!user ? "relative" : ""}>
                                <div className={!user ? "blur-[6px] pointer-events-none select-none opacity-90" : ""}>
                                    <EventCard event={e}
                                        onFollow={user ? handleFollow : null}
                                        onJoin={user ? handleJoin : null} />
                                </div>
                                {!user && (
                                    <button onClick={() => setRegisterPrompt(true)}
                                        className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 cursor-pointer"
                                        data-testid={`event-locked-${e.id}`}
                                        aria-label="Créer un compte pour voir cet événement">
                                        <span className="px-4 py-2 rounded-full bg-discover-gradient text-white text-sm font-bold shadow-md flex items-center gap-2">
                                            <Lock size={14} />Inscris-toi pour voir
                                        </span>
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {/* iter60 — "Voir plus" pagination button. Rendered when
                    the backend reports `has_more` for the current filter.
                    Loading state stays inline (no full-page spinner) so
                    users can keep browsing what's already on screen. */}
                {!loading && hasMore && events.length > 0 && (
                    <div className="flex justify-center mt-6">
                        <Button
                            onClick={loadMore}
                            disabled={loadingMore}
                            variant="outline"
                            className="rounded-full px-6"
                            data-testid="home-load-more">
                            {loadingMore ? "Chargement…" : `Voir plus (${totalEvents - events.length} restants)`}
                        </Button>
                    </div>
                )}
            </div>

            {/* Registration prompt for unauthenticated visitors */}
            <Dialog open={registerPrompt} onOpenChange={setRegisterPrompt}>
                <DialogContent className="rounded-3xl max-w-md" data-testid="register-prompt-dialog">
                    <DialogHeader>
                        <div className="mx-auto w-14 h-14 rounded-full bg-discover-gradient flex items-center justify-center mb-2">
                            <Lock className="text-white" size={26} />
                        </div>
                        <DialogTitle className="text-center text-xl">
                            Inscris-toi pour profiter pleinement
                        </DialogTitle>
                        <DialogDescription className="text-center pt-1">
                            Discoveur est gratuit. Crée ton compte pour découvrir tous les événements
                            publiés par la communauté discoveuriste, rejoindre, partager et discuter.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-col gap-2 pt-2">
                        <Button onClick={() => nav("/register")}
                            className="w-full rounded-full bg-discover-gradient text-white"
                            data-testid="register-prompt-go">
                            <UserPlus size={16} className="mr-2" />Créer mon compte gratuit
                        </Button>
                        <Button variant="outline" onClick={() => nav("/login")}
                            className="w-full rounded-full"
                            data-testid="register-prompt-login">
                            J'ai déjà un compte
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
